export const createAndParseHTMLString = (valueTitle: string) => {
  const h1 = document.createElement('h1');

  h1.style.fontFamily =
    'Helvetica, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol';
  h1.style.fontSize = '32px';
  h1.style.lineHeight = '40px';
  h1.style.letterSpacing = '0px';

  // Set content
  h1.textContent = valueTitle;

  // Return the HTML string
  return h1.outerHTML;
};

enum IMAGE_TYPES {
  JPEG = 'image/jpeg',
  JPG = 'image/jpg',
  PNG = 'image/png',
}

enum DOCUMENT_TYPES {
  PDF = 'application/pdf',
  DOC = 'application/msword',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  PPT = 'application/vnd.ms-powerpoint',
  PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  XLS = 'application/vnd.ms-excel',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ZIP = 'application/zip',
}

const IMAGES = {
  [IMAGE_TYPES.JPEG]: '.jpeg',
  [IMAGE_TYPES.JPG]: '.jpg',
  [IMAGE_TYPES.PNG]: '.png',
};
export const DOCUMENTS = {
  [DOCUMENT_TYPES.PDF]: '.pdf',
  [DOCUMENT_TYPES.DOC]: '.doc',
  [DOCUMENT_TYPES.DOCX]: '.docx',
  [DOCUMENT_TYPES.PPT]: '.ppt',
  [DOCUMENT_TYPES.PPTX]: '.pptx',
  [DOCUMENT_TYPES.XLS]: '.xls',
  [DOCUMENT_TYPES.XLSX]: '.xlsx',
  [DOCUMENT_TYPES.ZIP]: '.zip',
};

export const ACCEPTED_IMAGE_EXTENSIONS = Object.values({
  ...IMAGES,
}).join(',');

export const ACCEPTED_ALL_FILE_EXTENSIONS = Object.values({
  ...DOCUMENTS,
  ...IMAGES,
}).join(',');

// The format check reads only the extension of the name: some phones give a
// file with an empty type, and the Word type does not hold the text "docx"
// (TCSTC-4763). The match is exact, so ".doc" never passes ".docx".
export const isAcceptedExtension = (fileName: string, accept: string) => {
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex < 0 || dotIndex === fileName.length - 1) {
    return false;
  }

  const extension = fileName.slice(dotIndex).toLowerCase();
  return accept
    .split(',')
    .some(token => token.trim().toLowerCase() === extension);
};

const TYPE_BY_EXTENSION: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries({ ...DOCUMENTS, ...IMAGES }).map(([type, extension]) => [
      extension,
      type,
    ]),
  ),
  [IMAGES[IMAGE_TYPES.JPG]]: IMAGE_TYPES.JPEG,
};

// A phone can give a file with an empty type, and the file service refuses an
// upload with an empty content type (TCSTC-4763, AC-5). This gives the file
// the type of its extension. A file with a type, or with an unknown
// extension, stays as it is.
export const withTypeFromExtension = (file: File) => {
  if (file.type) {
    return file;
  }

  const dotIndex = file.name.lastIndexOf('.');
  const type =
    dotIndex < 0
      ? undefined
      : TYPE_BY_EXTENSION[file.name.slice(dotIndex).toLowerCase()];
  if (!type) {
    return file;
  }

  return new File([file], file.name, {
    type,
    lastModified: file.lastModified,
  });
};

export const withTypesFromExtension = (fileList: FileList) => {
  const files = Array.from(fileList);
  if (files.every(file => file.type)) {
    return fileList;
  }

  try {
    const dataTransfer = new DataTransfer();
    for (const file of files) {
      dataTransfer.items.add(withTypeFromExtension(file));
    }
    return dataTransfer.files;
  } catch {
    return fileList;
  }
};
